<script lang="ts">
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import BlogArticleHighlight from '../components/blogArticleHighlight.svelte';
	import type {
		SbBlogArticleCategoryStory,
		SbBlogArticleStory,
		SbBlogExternalArticleStory
	} from '../types';
	import { page } from '$app/state';
	import PageGrid from '../components/pageGrid.svelte';
	import BlogArticleLink from '../components/blogArticleLink.svelte';
	import BlogArticlePlaceholder from '../components/blogArticlePlaceholder.svelte';
	import log from '$lib/utils/log';
	import { fade } from 'svelte/transition';
	import { storyblok } from '$lib/state/storyblok.svelte';

	interface Props {
		content: ISbStoryData<{
			highlighted_article: SbBlogArticleStory;
			default_category_name: string;
		}>['content'];
	}

	let { content }: Props = $props();

	let highlightedArticle = $state<SbBlogArticleStory | null>(null);
	let categoryName = $state<string>();

	let articlesPromise = $state<Promise<Array<SbBlogArticleStory | SbBlogExternalArticleStory>>>();

	async function getArticles(
		category: SbBlogArticleCategoryStory | null | undefined
	): Promise<Array<SbBlogArticleStory | SbBlogExternalArticleStory>> {
		const filter_query: { [key: string]: any } = {
			component: {
				in: 'blog_page,blog_external_article'
			}
		};

		if (category) {
			filter_query.category = {
				in: category.uuid
			};
		}

		try {
			const root = page.route.id?.split('/')?.[1];

			const { data } = await storyblok.client.get('cdn/stories', {
				by_slugs: `${root}/*`,
				resolve_relations: ['blog_page.category'],
				filter_query,
				sort_by: 'content.custom_publish_date:desc',
				per_page: 100
			});

			// TODO: Move the filter to `filter_query` (otherwise, the pagination might be wrong)
			return (data.stories as Array<SbBlogArticleStory | SbBlogExternalArticleStory>).filter(
				(article) => article.uuid !== highlightedArticle?.uuid
			);
		} catch (e) {
			log.exception(e);
			return [];
		}
	}

	$effect.pre(() => {
		articlesPromise = getArticles(page.state.selectedBlogArticleCategory);
	});

	$effect.pre(() => {
		highlightedArticle = page.state.selectedBlogArticleCategory
			? page.state.selectedBlogArticleCategory.content?.highlighted_article
			: content.highlighted_article;
	});

	$effect.pre(() => {
		categoryName = page.state.selectedBlogArticleCategory
			? page.state.selectedBlogArticleCategory.content.name
			: content.default_category_name;
	});
</script>

<section use:editable={content} class="col-span-full">
	<PageGrid>
		{#if categoryName}
			<h1
				in:fade
				class="col-span-full py-6 text-[32px]/[110%] font-semibold tracking-[-0.32px] text-brown-1 lg:text-[56px]/[110%] lg:tracking-[-0.56px]"
			>
				{categoryName}
			</h1>
		{/if}
	</PageGrid>

	{#if highlightedArticle && typeof highlightedArticle === 'object'}
		<BlogArticleHighlight story={highlightedArticle} />
	{/if}

	<div class="my-14 lg:my-22">
		<PageGrid gridClass="gap-y-11 lg:gap-y-14">
			{#if articlesPromise}
				{#await articlesPromise}
					<div class="col-span-full lg:col-span-6">
						<BlogArticlePlaceholder />
					</div>
					<div class="col-span-full lg:col-span-6">
						<BlogArticlePlaceholder />
					</div>
				{:then articles}
					{#each articles as article}
						<div class="col-span-full lg:col-span-6">
							<BlogArticleLink story={article} />
						</div>
					{/each}
				{/await}
			{:else}
				<div class="col-span-full lg:col-span-6">
					<BlogArticlePlaceholder />
				</div>
				<div class="col-span-full lg:col-span-6">
					<BlogArticlePlaceholder />
				</div>
			{/if}
		</PageGrid>
	</div>
</section>
